export const SMARTWORK_WORKSPACE_RESOLVER_STORAGE_KEY = "openanalyst.smartwork.workspaceResolver.v1"
export const SMARTWORK_PATH_HINT_MAX_LENGTH = 120

const FALLBACK_PATH_HINT = "OpenAnalyst project"

const SMARTWORK_WORKSPACE_ID_HASH_LENGTH = 12

const utf8Encoder = typeof TextEncoder !== "undefined" ? new TextEncoder() : null

const toHex = (bytes: Uint8Array) => {
  let out = ""
  for (const byte of bytes) out += byte.toString(16).padStart(2, "0")
  return out
}

const sha256Hex = async (value: string): Promise<string> => {
  if (typeof crypto !== "undefined" && crypto.subtle && utf8Encoder) {
    const buffer = await crypto.subtle.digest("SHA-256", utf8Encoder.encode(value))
    return toHex(new Uint8Array(buffer))
  }
  const nodeCrypto = await import("node:crypto")
  return nodeCrypto.createHash("sha256").update(value).digest("hex")
}

// Must stay in lockstep with packages/smartwork/apps/server/src/workspaces.ts (workspaceIdForPath).
// Asserted by smartwork-project-identity.test.ts so the two implementations cannot drift.
export const smartworkWorkspaceIdForPath = async (workspacePath: string): Promise<string> => {
  const hex = await sha256Hex(workspacePath)
  return `ws_${hex.slice(0, SMARTWORK_WORKSPACE_ID_HASH_LENGTH)}`
}

export const isLegacySmartworkWorkspaceId = (id: string): boolean => !/^ws_[a-f0-9]{12}$/.test(id.trim())

export type SmartworkWorkspaceResolverRecord = {
  desktopId: string
  workspaceId: string
  workspacePath: string
  pathHint: string
  handle: string
  displayName: string
  projectTitle: string
  updatedAt: string
}

export type SmartworkWorkspaceResolverInput = {
  desktopId: string
  workspaceId: string
  workspacePath: string
  handle: string
  displayName: string
  projectTitle: string
  updatedAt?: string
}

const controlCharacters = /[\x00-\x1F\x7F]/
const commonHomeContainers = new Set([
  "Desktop",
  "Documents",
  "Downloads",
  "Projects",
  "Developer",
  "Workspace",
  "Workspaces",
])

const isSensitivePathSegment = (segment: string) => {
  const value = segment.toLowerCase()
  return value === "~" || value === ".ssh" || value === ".env" || value.includes("secret") || value.includes("token")
}

const capPathHint = (value: string) => value.slice(0, SMARTWORK_PATH_HINT_MAX_LENGTH).replace(/\/+$/, "")

export const smartworkSafePathHint = (value: unknown) => {
  if (typeof value !== "string") return ""
  if (controlCharacters.test(value)) return ""
  const normalized = value.trim().replace(/\\/g, "/").replace(/\/+/g, "/")
  if (normalized.startsWith("/") || /^[A-Za-z]:/.test(normalized)) return ""
  const segments = normalized.split("/").filter(Boolean)
  if (segments.length === 0) return ""
  if (segments.some((segment) => controlCharacters.test(segment) || isSensitivePathSegment(segment))) return ""
  if (["users", "home"].includes(segments[0]?.toLowerCase() ?? "")) return ""
  return capPathHint(segments.join("/"))
}

const safeHintSegments = (segments: string[]) => {
  if (segments.length === 0) return []
  const first = segments[0]
  const last = segments.at(-1)
  if (!last || isSensitivePathSegment(last)) return []
  if (first && commonHomeContainers.has(first) && segments.length > 1 && !isSensitivePathSegment(first))
    return [first, last]
  return [last]
}

export function smartworkPathHint(directory: string) {
  const normalized = directory.trim().replace(/\\/g, "/").replace(/\/+/g, "/")
  if (!normalized || controlCharacters.test(normalized)) return FALLBACK_PATH_HINT
  const segments = normalized
    .replace(/^[A-Za-z]:\/?/, "")
    .split("/")
    .filter(Boolean)
  const homeIndex = segments.findIndex(
    (segment, index) => ["Users", "home"].includes(segment) && index + 1 < segments.length,
  )
  const candidate =
    homeIndex === -1 ? safeHintSegments(segments.slice(-1)) : safeHintSegments(segments.slice(homeIndex + 2))
  const sanitized = candidate.filter((segment) => !isSensitivePathSegment(segment) && !controlCharacters.test(segment))
  if (sanitized.length === 0) return FALLBACK_PATH_HINT
  return capPathHint(sanitized.join("/")) || FALLBACK_PATH_HINT
}

const resolverKey = (desktopId: string, workspaceId: string, handle: string) => `${desktopId}\n${workspaceId}\n${handle}`

const resolverRecord = (value: unknown): SmartworkWorkspaceResolverRecord | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  const item = value as Record<string, unknown>
  const desktopId = typeof item.desktopId === "string" ? item.desktopId.trim() : ""
  const workspaceId = typeof item.workspaceId === "string" ? item.workspaceId.trim() : ""
  const workspacePath = typeof item.workspacePath === "string" ? item.workspacePath : ""
  if (!desktopId || !workspaceId || !workspacePath.trim()) return null
  return {
    desktopId,
    workspaceId,
    workspacePath,
    pathHint: smartworkSafePathHint(item.pathHint) || smartworkPathHint(workspacePath),
    handle: typeof item.handle === "string" ? item.handle.trim() : "",
    displayName: typeof item.displayName === "string" ? item.displayName.trim() : "",
    projectTitle: typeof item.projectTitle === "string" ? item.projectTitle.trim() : "",
    updatedAt: typeof item.updatedAt === "string" && item.updatedAt.trim() ? item.updatedAt.trim() : "",
  }
}

const readResolverRegistry = () => {
  if (typeof localStorage === "undefined") return {}
  const raw = localStorage.getItem(SMARTWORK_WORKSPACE_RESOLVER_STORAGE_KEY)
  if (!raw) return {}
  let parsed: unknown
  try {
    parsed = JSON.parse(raw) as unknown
  } catch {
    return {}
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {}
  return Object.fromEntries(
    Object.entries(parsed)
      .map(([key, value]) => [key, resolverRecord(value)] as const)
      .filter((entry): entry is readonly [string, SmartworkWorkspaceResolverRecord] => !!entry[1]),
  ) as Record<string, SmartworkWorkspaceResolverRecord>
}

const writeResolverRegistry = (value: Record<string, SmartworkWorkspaceResolverRecord>) => {
  if (typeof localStorage === "undefined") return
  localStorage.setItem(SMARTWORK_WORKSPACE_RESOLVER_STORAGE_KEY, JSON.stringify(value))
}

export function upsertSmartworkWorkspaceResolver(input: SmartworkWorkspaceResolverInput) {
  const desktopId = input.desktopId.trim()
  const workspaceId = input.workspaceId.trim()
  const handle = input.handle.trim().replace(/^@/, "")
  if (!desktopId || !workspaceId || !input.workspacePath.trim()) return null
  const record = {
    desktopId,
    workspaceId,
    workspacePath: input.workspacePath,
    pathHint: smartworkPathHint(input.workspacePath),
    handle,
    displayName: input.displayName.trim(),
    projectTitle: input.projectTitle.trim(),
    updatedAt: input.updatedAt?.trim() || new Date().toISOString(),
  } satisfies SmartworkWorkspaceResolverRecord
  writeResolverRegistry({
    ...readResolverRegistry(),
    [resolverKey(desktopId, workspaceId, handle)]: record,
  })
  return record
}

export type SmartworkWorkspaceResolverResolution =
  | { kind: "found"; record: SmartworkWorkspaceResolverRecord }
  | { kind: "not-found" }
  | { kind: "ambiguous"; candidates: SmartworkWorkspaceResolverRecord[] }

export function resolveSmartworkWorkspaceResolverDetailed(input: {
  workspaceId: string
  desktopId?: string | null
  handle?: string | null
}): SmartworkWorkspaceResolverResolution {
  const workspaceId = input.workspaceId.trim()
  if (!workspaceId) return { kind: "not-found" }
  const desktopId = input.desktopId?.trim()
  const handle = input.handle?.trim().replace(/^@/, "")
  const registry = readResolverRegistry()
  if (desktopId && handle) {
    const key = resolverKey(desktopId, workspaceId, handle)
    if (registry[key]) return { kind: "found", record: registry[key]! }
    const migrated = Object.values(registry).find(
      (record) => record.workspaceId === workspaceId && record.desktopId === desktopId && record.handle === handle,
    )
    if (!migrated) return { kind: "not-found" }
    writeResolverRegistry({
      ...Object.fromEntries(
        Object.entries(registry).filter(
          ([entryKey, record]) =>
            entryKey === key ||
            record.workspaceId !== workspaceId ||
            record.desktopId !== desktopId ||
            record.handle !== handle,
        ),
      ),
      [key]: migrated,
    })
    return { kind: "found", record: migrated }
  }
  const matches = Object.values(registry).filter(
    (record) =>
      record.workspaceId === workspaceId &&
      (!desktopId || record.desktopId === desktopId) &&
      (!handle || record.handle === handle),
  )
  if (matches.length === 0) return { kind: "not-found" }
  if (matches.length === 1) return { kind: "found", record: matches[0]! }
  return { kind: "ambiguous", candidates: matches }
}

export function resolveSmartworkWorkspaceResolver(input: {
  workspaceId: string
  desktopId?: string | null
  handle?: string | null
}): SmartworkWorkspaceResolverRecord | null {
  const result = resolveSmartworkWorkspaceResolverDetailed(input)
  return result.kind === "found" ? result.record : null
}

// Finds resolver records that share a (handle, projectTitle) identity but live under a
// different workspaceId. This detects the "user moved/renamed the project folder" case where
// the new directory hashes to a fresh workspaceId but the same conceptual project still has
// a stored resolver record from its old path.
//
// Returned candidates are sorted newest-first so a UI picker shows the most recent first.
// `excludeWorkspaceId` is the current/new workspaceId — never returned as a candidate.
export function findSmartworkResolverByProjectIdentity(input: {
  desktopId: string
  handle: string
  projectTitle: string
  excludeWorkspaceId?: string | null
}): SmartworkWorkspaceResolverRecord[] {
  const desktopId = input.desktopId.trim()
  const handle = input.handle.trim().replace(/^@/, "")
  const projectTitle = input.projectTitle.trim()
  if (!desktopId || !handle || !projectTitle) return []
  const excludeWorkspaceId = input.excludeWorkspaceId?.trim() ?? ""
  const registry = readResolverRegistry()
  return Object.values(registry)
    .filter(
      (record) =>
        record.desktopId === desktopId &&
        record.handle === handle &&
        record.projectTitle === projectTitle &&
        (!excludeWorkspaceId || record.workspaceId !== excludeWorkspaceId),
    )
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0))
}

// Atomically retire the old workspaceId binding and create a new one at the new path,
// preserving the (handle, projectTitle, displayName) identity. Returns the new record
// or null if input is invalid.
export function migrateSmartworkWorkspaceResolver(input: {
  desktopId: string
  fromWorkspaceId: string
  toWorkspaceId: string
  workspacePath: string
  handle: string
  displayName: string
  projectTitle: string
}): SmartworkWorkspaceResolverRecord | null {
  const desktopId = input.desktopId.trim()
  const fromWorkspaceId = input.fromWorkspaceId.trim()
  const toWorkspaceId = input.toWorkspaceId.trim()
  const handle = input.handle.trim().replace(/^@/, "")
  if (!desktopId || !fromWorkspaceId || !toWorkspaceId || !handle || !input.workspacePath.trim()) return null
  if (fromWorkspaceId === toWorkspaceId) return null
  const registry = readResolverRegistry()
  // Drop the old binding for THIS handle only — handle forks (same workspaceId, different
  // handle values) are independent records and may still be valid after migration. Callers
  // who want to migrate every handle at the old workspaceId must call migrate per handle.
  // Single setItem at the end applies the migration atomically.
  const filtered = Object.fromEntries(
    Object.entries(registry).filter(
      ([, record]) => !(record.workspaceId === fromWorkspaceId && record.desktopId === desktopId && record.handle === handle),
    ),
  )
  const next: SmartworkWorkspaceResolverRecord = {
    desktopId,
    workspaceId: toWorkspaceId,
    workspacePath: input.workspacePath,
    pathHint: smartworkPathHint(input.workspacePath),
    handle,
    displayName: input.displayName.trim(),
    projectTitle: input.projectTitle.trim(),
    updatedAt: new Date().toISOString(),
  }
  writeResolverRegistry({
    ...filtered,
    [resolverKey(desktopId, toWorkspaceId, handle)]: next,
  })
  return next
}

export function removeSmartworkWorkspaceResolver(input: {
  workspaceId: string
  desktopId?: string | null
  handle?: string | null
}) {
  const workspaceId = input.workspaceId.trim()
  if (!workspaceId) return false
  const desktopId = input.desktopId?.trim()
  const handle = input.handle?.trim().replace(/^@/, "")
  if (!desktopId || !handle) return false
  const registry = readResolverRegistry()
  const next = Object.fromEntries(
    Object.entries(registry).filter(
      ([, record]) => record.workspaceId !== workspaceId || record.desktopId !== desktopId || record.handle !== handle,
    ),
  )
  writeResolverRegistry(next)
  return Object.keys(next).length !== Object.keys(registry).length
}
